import React, { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import SiteHeader from '../../shared/SiteHeader';
import AdSense from '../../shared/AdSense';
import adultData from '../../data/devotionals/adult.json';
import childData from '../../data/devotionals/child.json';
import seniorData from '../../data/devotionals/senior.json';
import teenData from '../../data/devotionals/teen.json';
import { isAdsEnabled } from '../../utils/featureFlags';
import './SampleDevotional.css';

interface DevotionalData {
  role: string;
  content: string;
  messageId: string;
  conversationId: string;
  messageType: null;
  authorName: string;
  messageFeedback: null;
  isFavorite: boolean;
  isShared: boolean;
  timestamp: string;
}

const DEVOTIONALS: Record<string, DevotionalData> = {
  adult: adultData as unknown as DevotionalData,
  child: childData as unknown as DevotionalData,
  senior: seniorData as unknown as DevotionalData,
  teen: teenData as unknown as DevotionalData,
};

const DEFAULT_SLUG = 'adult';

const SampleDevotional: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const devotional = DEVOTIONALS[slug ?? ''];

  useEffect(() => {
    if (!devotional) {
      return;
    }
    const label = (slug ?? '').charAt(0).toUpperCase() + (slug ?? '').slice(1);
    document.title = `Walk It With Me - Sample Devotional (${label})`;
  }, [devotional, slug]);

  if (!devotional) {
    return <Navigate to={`/sample/devotional/${DEFAULT_SLUG}`} replace />;
  }

  return (
    <>
      <SiteHeader />
      <main className="container py-4">
        <div className="row">
          <div className="col-12 col-lg-9">
            <h1 className="h3 mb-1">Sample Devotional</h1>
            <p className="text-muted mb-4">
              Below is an example of a daily devotional generated by Walk It With Me.
            </p>

            <article className="mt-2" dangerouslySetInnerHTML={{ __html: devotional.content }} />

            {/* Horizontal ad — small/medium screens only */}
            {isAdsEnabled() && (
              <div className="d-block d-lg-none my-2 sample-ad-inline">
                <AdSense
                  adClient={process.env.REACT_APP_ADSENSE_CLIENT_ID || ''}
                  adSlot={process.env.REACT_APP_ADSENSE_SLOT_HORIZONTAL || ''}
                  adFormat="horizontal"
                  fullWidthResponsive={true}
                />
              </div>
            )}

            <div className="alert alert-secondary text-center" role="note">
              <p className="mb-2 fw-semibold">Get a personalized devotional every day.</p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Link to="/login" className="btn btn-primary">
                  Log In
                </Link>
                <Link to="/login#signup" className="btn btn-outline-primary">
                  Create a Free Account
                </Link>
              </div>
            </div>
          </div>
          {/* Vertical ad sidebar — large screens only */}
          <div className="col-lg-3 d-none d-lg-flex flex-column sample-ad-sidebar">
            {isAdsEnabled() && (
              <AdSense
                adClient={process.env.REACT_APP_ADSENSE_CLIENT_ID || ''}
                adSlot={process.env.REACT_APP_ADSENSE_SLOT_VERTICAL || ''}
                adFormat="auto"
              />
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default SampleDevotional;
